import React, { useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';

import useWarehouseService from '../../app/services/hooks/useWarehouseService';
import { WarehouseDataForm, WarehouseFilters } from '../../app/models/Warehouse';
import WarehouseDataTable from './components/tables/WarehouseDataTable';
import WarehouseCreate from './components/WarehouseCreate';
import WarehouseEdit from './components/WarehouseEdit';

const breadcrumbs: BreadcrumbsItem[] = [
  { name: 'almacenes', url: '/warehouses', isActive: true }
];

const Warehouses = () => {
  const { navigationPage } = useNavigationPage();
  const { fetchingGetWarehouses, getWarehouses, deleteWarehouse } = useWarehouseService();

  const [warehouses, setWarehouses] = useState<WarehouseDataForm[]>([]);
  const [warehouseIdEdit, setWarehouseIdEdit] = useState<number>(-1);
  const [showingEdit, setShowingEdit] = useState(false);
  const [showingCreate, setShowingCreate] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);

  const [warehouseFilter, setWarehouseFilter] = useState<WarehouseFilters>({
    page: 1, per_page: 10, sort: 'id', order: 'desc'
  });

  useEffect(() => { reloadTable(); }, [warehouseFilter]);

  const getAll = () => {
    getWarehouses(warehouseFilter, {
      onSuccess: (response: ServiceResponse) => {
        setWarehouses(response.data.warehouses);
        setTotalRows(response.data.total_rows);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message, { autoClose: 2000 });
        if (response.data?.to_dashboard) navigationPage('/');
      }
    });
  };

  const showEdit = (id: number) => { setShowingEdit(true); setWarehouseIdEdit(id); };
  const hideEdit = () => { setShowingEdit(false); setWarehouseIdEdit(-1); };
  const showCreate = () => setShowingCreate(true);
  const hideCreate = () => setShowingCreate(false);
  const reloadTable = () => getAll();

  const destroy = (id: number) => {
    const _text = 'Está a punto de eliminar el almacén #' + id;
    useReactConfirmAlert().requestConfirmation({
      title: '¿Estás seguro?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Si, confirmar',
          onClick: () => {
            deleteWarehouse(id, {
              onSuccess: (response: ServiceResponse) => {
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              useReactConfirmAlert().infoAlert({ title: 'Cancelado', message: 'El almacén no se ha eliminado.' });
            }, 0);
          }
        }
      }
    });
  };

  return (
    <>
      <Breadcrumbs pageSection="Almacenes" breadcrumbs={breadcrumbs} />

      <DefaultCard>
        <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} title="Nuevo almacén" />} />
        <WarehouseDataTable
          warehouses={warehouses}
          edit={showEdit}
          destroy={destroy}
          loading={fetchingGetWarehouses}
          filter={warehouseFilter}
          setFilter={setWarehouseFilter}
          totalRows={totalRows}
          callbackSuccess={reloadTable}
        />
      </DefaultCard>

      {showingCreate && (
        <DefaultModal show={showingCreate} handleClose={hideCreate} title="Crear almacén" backdrop={true} showFooter={false}>
          <WarehouseCreate onSaved={() => { reloadTable(); hideCreate(); }} onCancel={hideCreate} />
        </DefaultModal>
      )}

      {showingEdit && (
        <DefaultModal show={showingEdit} handleClose={hideEdit} title="Editar almacén" backdrop={true} showFooter={false}>
          <WarehouseEdit warehouseId={warehouseIdEdit} onSaved={() => { reloadTable(); hideEdit(); }} onCancel={hideEdit} onError={hideEdit} />
        </DefaultModal>
      )}
    </>
  );
};

export default Warehouses;
